import { PriceTrendPoint } from "api/services/valuations";

const WIDTH = 900;
const HEIGHT = 280;
const LEFT = 70;
const RIGHT = 24;
const TOP = 20;
const BOTTOM = 42;

const compactMoney = (value: number) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return String(Math.round(value));
};

const fullMoney = (value?: number) =>
  value ? `${Math.round(value).toLocaleString()} Birr` : "No price";

const linePath = (
  points: PriceTrendPoint[],
  field: "askingMedian" | "verifiedSaleMedian",
  xFor: (index: number) => number,
  yFor: (value: number) => number
) =>
  points
    .map((point, index) => ({ value: point[field], index }))
    .filter((point): point is { value: number; index: number } => Boolean(point.value))
    .map((point, index) => `${index ? "L" : "M"}${xFor(point.index)},${yFor(point.value)}`)
    .join(" ");

function PriceTrendChart({ points }: { points: PriceTrendPoint[] }) {
  const prices = points.flatMap((point) =>
    [point.askingMedian, point.verifiedSaleMedian].filter(
      (value): value is number => Boolean(value)
    )
  );
  if (!prices.length) return null;

  const rawMin = Math.min(...prices);
  const rawMax = Math.max(...prices);
  const spread = Math.max(rawMax - rawMin, rawMax * 0.08, 1);
  const minimum = Math.max(0, rawMin - spread * 0.15);
  const maximum = rawMax + spread * 0.15;
  const plotWidth = WIDTH - LEFT - RIGHT;
  const plotHeight = HEIGHT - TOP - BOTTOM;
  const xFor = (index: number) =>
    points.length === 1
      ? LEFT + plotWidth / 2
      : LEFT + (index / (points.length - 1)) * plotWidth;
  const yFor = (value: number) =>
    TOP + ((maximum - value) / Math.max(1, maximum - minimum)) * plotHeight;
  const askingPath = linePath(points, "askingMedian", xFor, yFor);
  const salePath = linePath(points, "verifiedSaleMedian", xFor, yFor);
  const labelIndexes = Array.from(
    new Set([0, Math.floor((points.length - 1) / 2), points.length - 1])
  );

  return (
    <div className="w-full overflow-x-auto">
      <svg
        className="min-w-[620px] w-full"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label="Vehicle price trend over time"
      >
        {[0, 1, 2, 3, 4].map((step) => {
          const y = TOP + (step / 4) * plotHeight;
          const value = maximum - (step / 4) * (maximum - minimum);
          return (
            <g key={step}>
              <line x1={LEFT} x2={WIDTH - RIGHT} y1={y} y2={y} stroke="#E2E8F0" />
              <text x={LEFT - 10} y={y + 4} textAnchor="end" fontSize="11" fill="#64748B">
                {compactMoney(value)}
              </text>
            </g>
          );
        })}
        {askingPath ? <path d={askingPath} fill="none" stroke="#EF3E42" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /> : null}
        {salePath ? <path d={salePath} fill="none" stroke="#16A05D" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /> : null}
        {points.map((point, index) => (
          <g key={point.date}>
            {point.askingMedian ? (
              <circle cx={xFor(index)} cy={yFor(point.askingMedian)} r="4" fill="#EF3E42">
                <title>{`${point.date}: advertised median ${fullMoney(point.askingMedian)} from ${point.askingCount} price(s)`}</title>
              </circle>
            ) : null}
            {point.verifiedSaleMedian ? (
              <circle cx={xFor(index)} cy={yFor(point.verifiedSaleMedian)} r="4" fill="#16A05D">
                <title>{`${point.date}: verified sale median ${fullMoney(point.verifiedSaleMedian)} from ${point.verifiedSaleCount} sale(s)`}</title>
              </circle>
            ) : null}
          </g>
        ))}
        {labelIndexes.map((index) => (
          <text key={index} x={xFor(index)} y={HEIGHT - 12} textAnchor="middle" fontSize="11" fill="#64748B">
            {new Date(`${points[index].date}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </text>
        ))}
      </svg>
    </div>
  );
}

export default PriceTrendChart;
